import "../styles/job.css";
import { lazy, Suspense } from "react";

import PowerBIHero from "./PowerBIHero";
import PowerBIJobRoles from "./PowerBIJobRoles";

/* 🔥 Lazy-load heavy sections to avoid blocking navigation */
const Demand = lazy(() => import("../pages/PowerBIDemand.js"));
const FeaturesSection = lazy(() => import("../pages/FeaturesSection"));
const ScrollingFeedback = lazy(() => import("../pages/ScrollingFeedback"));

export default function PowerBIOverview() {
  return (
    <>
      {/* ================= HERO BANNER ================= */}
      <PowerBIHero />

      {/* ================= MAIN CONTENT ================= */}
      <section className="genai-page">

        {/* PAGE INTRO */}
        <header className="genai-hero">
          <h1>Power BI Data Analyst – Business Intelligence & Data Visualization</h1>
          <p className="subtitle">
            Transform Business Data into Actionable Insights using Microsoft Power BI
          </p>
        </header>

        {/* PROGRAM OVERVIEW */}
        <section className="genai-section">
          <p>
            A Power BI Data Analyst is a professional who uses Microsoft Power BI
            to analyze data and convert it into meaningful insights for businesses.
            They collect data from multiple sources such as databases, Excel files,
            and cloud platforms and prepare it for analysis.
          </p>

          <p>
            Using Power Query, analysts clean and transform data, build
            relationships between datasets, and create powerful data models.
            They design interactive dashboards, charts, and reports that help
            organizations identify trends, monitor performance, and make
            data-driven decisions to improve business strategies.
          </p>
        </section>

        {/* JOB ROLES */}
        <PowerBIJobRoles />

        {/* LEARNING OUTCOMES */}
        <section className="genai-section">
          <h2>Key Learning Outcomes</h2>
          <ul className="outcome-list">
            <li>Understanding data analysis concepts and business intelligence fundamentals</li>
            <li>Ability to use Microsoft Power BI for data visualization and reporting</li>
            <li>Skills in data cleaning and transformation using Power Query</li>
            <li>Knowledge of creating data models and relationships between datasets</li>
            <li>Ability to write and use DAX formulas for advanced data analysis</li>
            <li>Designing interactive dashboards and business reports</li>
            <li>Interpreting insights to support data-driven decision making</li>
          </ul>
        </section>

        {/* WHO SHOULD ATTEND */}
        <section className="genai-section">
          <h2>Who Should Attend</h2>
          <ul className="outcome-list">
            <li>Students and fresh graduates interested in data analytics</li>
            <li>IT professionals who want to move into data analytics or BI roles</li>
            <li>Software developers who want to learn data visualization</li>
            <li>Business professionals interested in data-driven decision making</li>
            <li>Anyone interested in learning Microsoft Power BI and analytics</li>
          </ul>

          <p className="salary-note">
            No advanced programming knowledge is required to start learning Power BI.
          </p>
        </section>

        {/* CAREER PATHWAYS */}
<section className="genai-section">
  <h2>Career Pathways, Job Roles & Salary Range in India</h2>

  <div className="salary-table-wrapper">
    <table className="salary-table">
      <thead>
        <tr>
          <th>Job Role</th>
          <th>*Entry Salary</th>
          <th>*Mid-Level Salary</th>
        </tr>
      </thead>

      <tbody>
        <tr>
          <td>Data Analyst (Power BI)</td>
          <td>₹3 – 6 LPA</td>
          <td>₹6 – 12 LPA</td>
        </tr>

        <tr>
          <td>Power BI Developer</td>
          <td>₹4 – 8 LPA</td>
          <td>₹8 – 16 LPA</td>
        </tr>

        <tr>
          <td>Business Intelligence (BI) Analyst</td>
          <td>₹4 – 7 LPA</td>
          <td>₹7 – 15 LPA</td>
        </tr>

        <tr>
          <td>Reporting Analyst</td>
          <td>₹3 – 6 LPA</td>
          <td>₹6 – 11 LPA</td>
        </tr>

        <tr>
          <td>Data Visualization Specialist</td>
          <td>₹4 – 7 LPA</td>
          <td>₹8 – 14 LPA</td>
        </tr>

        <tr>
          <td>Senior Data Analyst</td>
          <td>₹6 – 12 LPA</td>
          <td>₹12 – 20+ LPA</td>
        </tr>
      </tbody>
    </table>

    <p className="salary-note">
      *Salary ranges vary based on experience, skills, projects, company size,
      and industry demand.
    </p>
  </div>
</section>

        {/* PROJECT HIGHLIGHT */}
        <section className="genai-section highlight">
          <h2>Project Highlights</h2>
          <p>
            <strong>Power BI Business Intelligence Dashboard:</strong> An
            interactive analytics project where learners transform raw
            business data into meaningful dashboards and reports using
            Microsoft Power BI. The project integrates multiple data sources,
            performs data cleaning and modeling, and applies DAX calculations
            to generate KPIs. It enables users to analyze trends, monitor
            performance, and make informed business decisions through
            dynamic visualizations.
          </p>
        </section>

        {/* ================= LAZY LOADED SECTIONS ================= */}
        <Suspense fallback={<div style={{ padding: 40 }}>Loading insights…</div>}>
          <Demand />
          <FeaturesSection />
          <ScrollingFeedback />
        </Suspense>

      </section>
    </>
  );
}